import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/components/ButtonComponent';
import { LineSeparator } from '@/components/LineSeparator';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { GASbkEvents } from '@/feature/analytics/sbk/ga-sbk-events';
import { useBetSubmissionComplete } from '@/feature/betslip-sbk/components/betslip-action-tray/hooks/use-bet-submission-complete';
import { useHandleBetSubmissionErrors } from '@/feature/betslip-sbk/components/betslip-action-tray/hooks/use-handle-bet-submission-errors';
import { useTotalStakeValidation } from '@/feature/betslip-sbk/components/betslip-num-pad/hooks/use-total-stake-validation';
import { useOddsChangeModal } from '@/feature/betslip-sbk/components/odds-change-modal/OddsChangeModalProvider';
import { OneLineSummary } from '@/feature/betslip-sbk/components/one-line-summary/OneLineSummary';
import { useIsBetSlipSubmitting } from '@/feature/betslip-sbk/hooks/betslip-hooks';
import { useSbkBetSlipStore } from '@/feature/betslip-sbk/hooks/use-sbk-betslip-store';
import { SBK_BETSLIP_LOG_TAG } from '@/feature/betslip-sbk/hooks/use-sbk-betslip-store';
import { delay } from '@/feature/betslip-sbk/utils/bet-submission/poll-submitted-bets';
import {
    isBetSlipValidSelector,
    showOddsChangeButtonSelector,
    totalBetCountSelector,
    totalBetrBucksStakeSelector,
    totalCashStakeSelector,
    totalStakeSelector,
} from '@/feature/betslip-sbk/utils/betslip-selectors';
import { useAgeCohortValidation } from '@/hooks/use-age-cohort-validation';
import { isUserBelowAgeLimitToPlaySelector, useAuthUserConfig, userIdSelector } from '@/hooks/use-auth-user-config';
import { useIsPredictionsProduct } from '@/hooks/use-is-predictions-product';
import { useParentGestureLock } from '@/hooks/use-parent-gesture-lock';
import { logger } from '@/utils/logging';
import { useWebsocket } from '@/utils/websocket/provider';

import { PlaceBetButtonLabel } from './PlaceBetButtonLabel';

const styles = StyleSheet.create({
    separator: {
        position: 'absolute',
        left: 0,
        right: 0,
    },
});

export const ACTION_TRAY_MAX_HEIGHT = 150;

export const BetSlipActionTray = () => {
    const { t } = useTranslation(['betslip_sbk', 'common']);
    const { websocketService } = useWebsocket();
    const isPredictions = useIsPredictionsProduct();
    const isBetSlipValid = useSbkBetSlipStore(isBetSlipValidSelector);
    const totalStake = useSbkBetSlipStore(totalStakeSelector);
    const totalBetCount = useSbkBetSlipStore(totalBetCountSelector);
    const totalCashStake = useSbkBetSlipStore(totalCashStakeSelector);
    const totalBetrBucksStake = useSbkBetSlipStore(totalBetrBucksStakeSelector);
    const validateTotalStake = useTotalStakeValidation();

    useEffect(() => {
        validateTotalStake(totalCashStake, totalBetrBucksStake);
    }, [totalCashStake, totalBetrBucksStake, validateTotalStake]);
    const showOddsChangeButton = useSbkBetSlipStore(showOddsChangeButtonSelector);
    const placeBets = useSbkBetSlipStore(state => state.actions.placeBets);
    const pollSubmittedBets = useSbkBetSlipStore(state => state.actions.pollSubmittedBets);
    const isBetSlipSubmitting = useIsBetSlipSubmitting();
    const onBetSubmissionComplete = useBetSubmissionComplete();
    const handleBetSubmissionErrors = useHandleBetSubmissionErrors();
    const { data: userId } = useAuthUserConfig({ select: userIdSelector });
    const { data: isBelowAgeLimitToPlay } = useAuthUserConfig({ select: isUserBelowAgeLimitToPlaySelector });
    const { showOddsChangeModal } = useOddsChangeModal();
    const { showAgeRestrictionNotice } = useAgeCohortValidation();
    const { bottom } = useSafeAreaInsets();

    const hasBetsWithStake = totalBetCount > 0;
    const isSubmittingRef = useRef(false);

    const handleOnPress = async () => {
        if (isBetSlipSubmitting || isSubmittingRef.current) {
            return;
        }

        isSubmittingRef.current = true;

        try {
            GASbkEvents.clickPlaceBet();

            if (isBelowAgeLimitToPlay) {
                showAgeRestrictionNotice();
                return;
            }

            if (userId) {
                if (websocketService?.isAblyDown()) {
                    logger.warn(SBK_BETSLIP_LOG_TAG, 'Bet submission warning', 'Ably disconnected');
                }
                await placeBets(userId, handleBetSubmissionErrors);
                await delay(3000);
                pollSubmittedBets(onBetSubmissionComplete);
            }
        } finally {
            isSubmittingRef.current = false;
        }
    };

    const showMultiplierChangesInfoSheet = () => {
        GASbkEvents.reviewMultiplierChanges('review_changes_first');
        showOddsChangeModal();
    };

    const getButtonProps = () => {
        if (showOddsChangeButton) {
            return {
                disabled: false,
                hierarchy: 'primary' as const,
                label: t(isPredictions ? 'review_price_changes' : 'review_odds_changes'),
                onPress: showMultiplierChangesInfoSheet,
                loading: false,
            };
        }
        if (!hasBetsWithStake) {
            return {
                disabled: true,
                hierarchy: 'primaryLilac' as const,
                label: t(isPredictions ? 'place_trade' : 'place_entry'),
            };
        }
        return {
            disabled: !isBetSlipValid,
            hierarchy: 'primaryLilac' as const,
            label: (
                <PlaceBetButtonLabel totalBetCount={totalBetCount} totalStake={totalStake} disabled={!isBetSlipValid} />
            ),
            onPress: handleOnPress,
            loading: isBetSlipSubmitting,
        };
    };

    const { onPress, label, loading, disabled, hierarchy } = getButtonProps();
    useParentGestureLock(loading ?? false);

    return (
        <Box
            position="absolute"
            backgroundColor={ds.colorToken.background.neutral.subtler}
            bottom={0}
            left={0}
            right={0}
            style={{ paddingBottom: bottom }}
            // Raise above the SubmissionOverlay while submitting (PIC-8532); otherwise keep it
            // below bottom-sheet modals (e.g. odds-change sheet) so they cover the tray (SBK-2467).
            zIndex={isBetSlipSubmitting ? 10 : 0}
        >
            <LineSeparator style={styles.separator} />
            <Box padding="s16" gap="s16">
                {showOddsChangeButton ? null : <OneLineSummary />}
                <Button
                    loading={loading}
                    label={label}
                    hierarchy={hierarchy}
                    onPress={onPress}
                    disabled={disabled}
                    loaderColors={submitLoaderColors}
                />
            </Box>
        </Box>
    );
};

const submitLoaderColors = { main: ds.colorValue.icon.primary, second: 'transparent' };
